import { format, isValid, parseISO } from 'date-fns'
import * as React from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router'

import { encodeQueryString } from '../../../../app/queryString'
import {
  CalendarView,
  CalendarViewHasParams,
  CalendarViewParam,
  CalendarViewsParams,
  ISO_DATEONLY_FORMAT,
} from '../constants'
import { getCalendarTradingDay } from '../utils'

const CALENDAR_BASE_PATH = '/calendar/'

function parseDateParam(dateString: string | null): Date | null {
  if (dateString) {
    const parsedDate = parseISO(dateString)
    return isValid(parsedDate) ? parsedDate : null
  }
  return null
}

function formatQueryParam(key: CalendarViewParam, value: any): any {
  if (key === CalendarViewParam.DateFrom) {
    return value ? format(value as Date, ISO_DATEONLY_FORMAT) : undefined
  }
  return value
}

function getCalendarViewSpecificParams<
  T extends CalendarView,
  ParamsType extends Partial<CalendarViewsParams[T]> = Partial<CalendarViewsParams[T]>,
>(params: ParamsType, view: T, formatFunc?: (key: CalendarViewParam, value: any) => any) {
  return Object.keys(params).reduce(
    (acc, key) =>
      CalendarViewHasParams[view]?.includes(key as CalendarViewParam)
        ? {
            ...acc,
            [key]: formatFunc
              ? formatFunc(key as CalendarViewParam, params[key as keyof ParamsType])
              : params[key as keyof ParamsType],
          }
        : acc,
    {} as ParamsType
  )
}

export function useCalendarRouter<T extends CalendarView>() {
  const [params, setSearchParams] = useSearchParams()
  const location = useLocation()
  const { ticker } = useParams()
  const view = React.useMemo(() => location.pathname.replace(CALENDAR_BASE_PATH, '') as T, [location])

  const setViewParams = React.useCallback(
    (newParams: Partial<CalendarViewsParams[T]>) => {
      setSearchParams((prev) => {
        const newQuery = new URLSearchParams(prev)
        Object.entries(newParams).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            newQuery.delete(key)
          } else if (value instanceof Date) {
            newQuery.set(key, format(value, ISO_DATEONLY_FORMAT))
          } else {
            newQuery.set(key, String(value))
          }
        })
        return newQuery
      })
    },
    [setSearchParams]
  )

  return React.useMemo(() => {
    const parsedParams: CalendarViewsParams[T] = {
      [CalendarViewParam.DateFrom]: parseDateParam(params.get(CalendarViewParam.DateFrom)) ?? getCalendarTradingDay(),
      [CalendarViewParam.Ticker]: ticker,
      [CalendarViewParam.Page]: Number(params.get(CalendarViewParam.Page) || '1'),
      [CalendarViewParam.Sort]: params.get(CalendarViewParam.Sort) ?? undefined,
    }

    return {
      view,
      params,
      setViewParams,
      viewParams: getCalendarViewSpecificParams(parsedParams, view),
      getCalendarLink: <View extends CalendarView>({
        view,
        params = {},
        additionalQueryParams,
      }: {
        view: View
        params?: Partial<CalendarViewsParams[View]>
        additionalQueryParams?: ObjectHash
      }) =>
        `/calendar/${view}?${encodeQueryString({
          ...getCalendarViewSpecificParams({ ...params }, view, formatQueryParam),
          ...additionalQueryParams,
        })}`,
    }
  }, [params, setViewParams, view, ticker])
}
